"use client";

import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format, subDays } from "date-fns";
import type { DateRange } from "react-day-picker";

import { cn } from "@/utils/common/cn";
import { Button } from "@/components/common/button";
import { Calendar } from "@/components/common/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/common/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/common/select";

import { useAppFilters } from "@/contexts/AppFilters";
import { getUserInstances, type UserInstance } from "@/api/instances";

export function InstanceControls() {
  const { instance, setInstance, dateRange, setDateRange } = useAppFilters();

  const [instances, setInstances] = React.useState<UserInstance[]>([]);
  const [loadingInstances, setLoadingInstances] = React.useState(false);

  // Fetch instances for the user
  React.useEffect(() => {
    const fetchInstances = async () => {
      try {
        setLoadingInstances(true);
        const data = await getUserInstances();

        if (data && data.length > 0) {
          setInstances(data);

          if (!instance) {
            setInstance(data[0].instance_id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch instances:", error);
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [instance, setInstance]);

  // Default: last 7 days
  React.useEffect(() => {
    if (!dateRange) {
      const today = new Date();
      setDateRange({
        from: subDays(today, 6),
        to: today,
      });
    }
  }, [dateRange, setDateRange]);

  const [open, setOpen] = React.useState(false);

  const label = React.useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      const sameMonth =
        dateRange.from.getMonth() === dateRange.to.getMonth() &&
        dateRange.from.getFullYear() === dateRange.to.getFullYear();

      const fromFmt = format(dateRange.from, "MMM d");
      const toFmt = sameMonth ? format(dateRange.to, "d") : format(dateRange.to, "MMM d");
      return `${fromFmt} – ${toFmt}`;
    }

    if (dateRange?.from) return format(dateRange.from, "MMM d");
    return "Select dates";
  }, [dateRange]);

  /**
   * Behavior requested:
   * - Any click starts a NEW range selection.
   * - First click sets "from" only.
   * - Second click sets "to" and closes popover.
   */
  const [isPickingEnd, setIsPickingEnd] = React.useState(false);

  // Keep local picking state in sync if dateRange changes externally
  React.useEffect(() => {
    const hasCompleteRange = !!(dateRange?.from && dateRange?.to);
    const hasStartOnly = !!(dateRange?.from && !dateRange?.to);

    if (hasCompleteRange) setIsPickingEnd(false);
    else if (hasStartOnly) setIsPickingEnd(true);
    else setIsPickingEnd(false);
  }, [dateRange?.from, dateRange?.to]);

  const startNewRange = React.useCallback(
    (day: Date) => {
      setDateRange({ from: day, to: undefined });
      setIsPickingEnd(true);
    },
    [setDateRange]
  );

  const finishRange = React.useCallback(
    (day: Date) => {
      const from = dateRange?.from ?? day;

      // Ensure from <= to (swap if needed)
      const next: DateRange =
        day < from ? { from: day, to: from } : { from, to: day };

      setDateRange(next);
      setIsPickingEnd(false);
      setOpen(false);
    },
    [dateRange?.from, setDateRange]
  );

  const handleDayClick = (day: Date) => {
    // Any click starts a new range IF we are not currently picking an end date.
    if (!isPickingEnd) {
      startNewRange(day);
      return;
    }

    // If we are picking the end, second click finishes the range.
    finishRange(day);
  };

  return (
    <div className="space-y-2 text-sidebar-foreground">
      {/* Label */}
      <p className="text-xs font-medium opacity-80">Instance</p>

      <div className="space-y-2">
        {/* Instance select */}
        <Select value={instance ?? undefined} onValueChange={setInstance}>
          <SelectTrigger
            disabled={loadingInstances || instances.length === 0}
            className={cn(
              "w-full rounded-2xl text-sm",
              "bg-sidebar text-white border border-gray-600",
              "focus:outline-none focus:ring-2 focus:ring-white/70 focus:ring-offset-0"
            )}
          >
            <SelectValue
              placeholder={loadingInstances ? "Loading instances..." : "Select instance"}
            />
          </SelectTrigger>

          <SelectContent className={cn("bg-sidebar text-white border border-white rounded-2xl")}>
            {instances.map((inst) => (
              <SelectItem
                key={inst.instance_id}
                value={inst.instance_id}
                className="data-[highlighted]:bg-white/10 data-[highlighted]:text-white focus:bg-white/10 focus:text-white"
              >
                {inst.instance_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date range */}
        <Popover open={open} onOpenChange={(next) => {
          setOpen(next);
          // When closing, reset picking state so next open starts fresh
          if (!next) setIsPickingEnd(false);
        }}>
          <PopoverTrigger asChild>
            <Button
              id="date"
              variant="outline"
              className={cn(
                "w-full justify-between rounded-2xl text-left text-sm font-normal",
                "bg-sidebar text-white border border-gray-600",
                "hover:bg-inherit hover:text-inherit hover:border-gray-600",
                !dateRange && "opacity-70"
              )}
            >
              <span>{label}</span>
              <CalendarIcon className="h-4 w-4 opacity-80" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            className={cn(
              "w-auto p-0 rounded-2xl",
              "bg-white text-gray-900 border border-gray-200 shadow-lg"
            )}
          >
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              // We handle clicks ourselves to enforce "any click starts a new range"
              onSelect={undefined as unknown as never}
              onDayClick={handleDayClick}
              numberOfMonths={1}
              className="rounded-2xl bg-white text-gray-900"
            />

            {/* Optional helper text (remove if you don't want it) */}
            <div className="px-3 pb-3 pt-2 text-xs text-gray-600">
              {!isPickingEnd ? "Click any date to start a new range." : "Click an end date to finish the range."}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
